/**
 * O domínio da tela de faturamento (`TP-052`, T9) no front, em pt-BR.
 *
 * O contrato da API vem em inglês nos TOKENS de enum (nome do membro C#,
 * `DI11` do TP-049) e em pt-BR nos NOMES de campo — é traduzido UMA vez, em
 * `mapeadores.ts`, logo depois do fetch, mesma disciplina de
 * `features/contratos/api/tipos.ts`.
 *
 * A seção `*Api` abaixo é a forma CRUA, escrita à mão: espelha
 * `contracts/openapi/anery-api.v1.json` (schemas `ConferenciaResponse`,
 * `ConferenciaResumoResponse`, `DetalheConferenciaResponse`,
 * `GuiaResponse`/`GuiaDetalheResponse`, `LinhaDetalheResponse`,
 * `MotivoFaturamentoResponse`, `DecisaoDetalheResponse`,
 * `DecisaoResponse`) e o endpoint real (`FaturamentoEndpoints.cs`) linha a
 * linha — a geração de `@anery/api-contracts` para este bloco não rodou
 * neste worktree (mesma nota de `features/contratos/api/tipos.ts`).
 */

// ------------------------------------------------------------- forma da API

/** `BillingReviewState` (`Anery.Domain.Faturamento`). */
export type EstadoDaConferenciaApi = 'UnderReview' | 'Secured' | 'Released' | 'Rejected';

/** `BillingReviewAction` (`Anery.Domain.Faturamento`). */
export type AcaoDeDecisaoApi = 'Release' | 'CorrectedInIw';

export interface GuiaResumoApi {
  readonly referenciaGuia: string | null;
  readonly avaliacaoId: string;
  readonly ok: number;
  readonly alerta: number;
  readonly barrado: number;
  readonly semCobertura: number;
}

/** O que `POST /faturamento/conferencias` devolve — `ConferenciaResponse`. */
export interface ConferenciaImportadaApi {
  readonly conferenciaId: string;
  readonly estado: EstadoDaConferenciaApi;
  readonly motivoRecusa: string | null;
  readonly guias: readonly GuiaResumoApi[] | null;
}

/** Um item de `GET /faturamento/conferencias` — `ConferenciaResumoResponse`. */
export interface ConferenciaResumoApi {
  readonly id: string;
  readonly contratoId: string;
  readonly competencia: string;
  readonly estado: EstadoDaConferenciaApi;
  readonly motivoRecusa: string | null;
  readonly criadoEm: string;
  readonly ok: number;
  readonly alerta: number;
  readonly barrado: number;
  readonly semCobertura: number;
}

export interface MotivoFaturamentoApi {
  readonly regraContratualId: string | null;
  readonly mensagem: string;
  readonly fonte: string | null;
}

/**
 * `estado` é `EstadoDaLinha.ToString()` do lado da API (`Anery.Motor`) —
 * texto livre no contrato (`string`, não um `$ref` de enum), por isso o
 * mapeamento tolera um token desconhecido (`mapearEstadoDaLinha`).
 */
export interface LinhaDetalheApi {
  readonly linha: number;
  readonly codigo: string | null;
  readonly estado: string | null;
  readonly motivos: readonly MotivoFaturamentoApi[] | null;
}

export interface GuiaDetalheApi {
  readonly referenciaGuia: string | null;
  readonly avaliacaoId: string;
  readonly linhas: readonly LinhaDetalheApi[] | null;
}

export interface DecisaoDetalheApi {
  readonly avaliacaoId: string;
  readonly linha: number;
  readonly acao: AcaoDeDecisaoApi;
  readonly motivo: string | null;
  readonly decididoPor: string | null;
  readonly decididoEm: string;
}

/** `GET /faturamento/conferencias/{id}` — `DetalheConferenciaResponse`. */
export interface DetalheConferenciaApi {
  readonly id: string;
  readonly contratoId: string;
  readonly competencia: string;
  readonly estado: EstadoDaConferenciaApi;
  readonly motivoRecusa: string | null;
  readonly guias: readonly GuiaDetalheApi[] | null;
  readonly decisoes: readonly DecisaoDetalheApi[] | null;
}

/** `POST /faturamento/conferencias/{id}/decisoes` — `DecisaoResponse`. */
export interface DecisaoResponseApi {
  readonly estado: EstadoDaConferenciaApi;
}

// -------------------------------------------------------- domínio do front

export type EstadoDaConferencia = 'em_conferencia' | 'segurada' | 'liberada' | 'recusada';

export const ROTULOS_DE_ESTADO_DA_CONFERENCIA: Record<EstadoDaConferencia, string> = {
  em_conferencia: 'Em conferência',
  segurada: 'Segurada',
  liberada: 'Liberada',
  recusada: 'Recusada',
};

/**
 * `EstadoDaLinha` (`Anery.Motor.Saida`), traduzido. `desconhecido` existe
 * porque o campo é `string` livre no contrato — mesma disciplina de
 * `EtapaConhecidaOuNao` (`features/falhas-de-email/api/tipos.ts`): um token
 * novo que a API mandar nunca vira `OK` por engano (`D7` do `BR-008`).
 */
export type EstadoDaLinha = 'ok' | 'alerta' | 'barrado' | 'sem_cobertura' | 'desconhecido';

export const ROTULOS_DE_ESTADO_DA_LINHA: Record<EstadoDaLinha, string> = {
  ok: 'OK',
  alerta: 'Alerta',
  barrado: 'Barrado',
  sem_cobertura: 'Sem cobertura',
  desconhecido: 'Estado desconhecido',
};

export type AcaoDeDecisao = 'liberar' | 'corrigido_no_iw';

export const ROTULOS_DE_ACAO: Record<AcaoDeDecisao, string> = {
  liberar: 'Liberar',
  corrigido_no_iw: 'Corrigido no IW',
};

export interface GuiaResumo {
  readonly referenciaGuia: string;
  readonly avaliacaoId: string;
  readonly ok: number;
  readonly alerta: number;
  readonly barrado: number;
  readonly semCobertura: number;
}

export interface ConferenciaImportada {
  readonly conferenciaId: string;
  readonly estado: EstadoDaConferencia;
  readonly motivoRecusa: string | null;
  readonly guias: readonly GuiaResumo[];
}

export interface ConferenciaResumo {
  readonly id: string;
  readonly contratoId: string;
  readonly competencia: string;
  readonly estado: EstadoDaConferencia;
  readonly motivoRecusa: string | null;
  readonly criadoEm: string;
  readonly ok: number;
  readonly alerta: number;
  readonly barrado: number;
  readonly semCobertura: number;
}

export interface Motivo {
  readonly regraContratualId: string | null;
  readonly mensagem: string;
  readonly fonte: string | null;
}

export interface LinhaDoVeredito {
  readonly linha: number;
  readonly codigo: string;
  readonly estado: EstadoDaLinha;
  readonly motivos: readonly Motivo[];
}

export interface GuiaDetalhada {
  readonly referenciaGuia: string;
  readonly avaliacaoId: string;
  readonly linhas: readonly LinhaDoVeredito[];
}

export interface Decisao {
  readonly avaliacaoId: string;
  readonly linha: number;
  readonly acao: AcaoDeDecisao;
  readonly motivo: string;
  readonly decididoPor: string | null;
  readonly decididoEm: string;
}

export interface DetalheDaConferencia {
  readonly id: string;
  readonly contratoId: string;
  readonly competencia: string;
  readonly estado: EstadoDaConferencia;
  readonly motivoRecusa: string | null;
  readonly guias: readonly GuiaDetalhada[];
  readonly decisoes: readonly Decisao[];
}

/** Chave de uma decisão já tomada — casa `Decisao` com `LinhaDoVeredito` sem precisar de outra tabela. */
export function chaveDaDecisao(avaliacaoId: string, linha: number): string {
  return `${avaliacaoId}:${String(linha)}`;
}
